import { z } from 'zod';
/**
 * Nummernkreise der Finanzen (KON-139). Belege tragen Kennung, Jahr und eine laufende Nummer (RE-2026-0001), wie Angebote (AN) und Aufträge (AU). Die Nummer wird erst
 * beim Festschreiben vergeben, nie beim Entwurf, damit keine Lücken entstehen. Personenkonten (Debitoren, Kreditoren) folgen dem Kontenrahmen: Bei vierstelligen
 * Sachkonten sind Debitoren 10000 bis 69999 und Kreditoren 70000 bis 99999, bei längeren Konten entsprechend mehr Stellen.
 */
export const DOCUMENT_NUMBER_PREFIXES = {
    invoice_out: 'RE',
    credit_note: 'GS',
    cancellation: 'ST',
};
/** RE-2026-0001 */
export function formatDocumentNumber(kind, year, sequence) {
    return `${DOCUMENT_NUMBER_PREFIXES[kind]}-${year}-${String(sequence).padStart(4, '0')}`;
}
// ---------------------------------------------------------------------------
// Einstellbare Nummerierung (Änderungswunsch vom 17.10.2026): Einrichtung → Rechnungsnummern
// ---------------------------------------------------------------------------
/**
 * year:   RE-2026-0001, die Serie beginnt mit jedem Jahr neu (so war es bisher)
 * customer: RE-10001-0001, mit der Kundennummer des Rechnungsempfängers (seine Debitorennummer), die Serie läuft ohne Unterbrechung weiter und gilt für alle Kunden gemeinsam
 * plain:  RE-0001, nur die Nummernserie, sie läuft ohne Unterbrechung weiter
 * Die Kennung der Belegart (RE, GS, ST) steht immer vorn, damit Rechnung, Gutschrift und Storno nie dieselbe Nummer tragen.
 */
export const NUMBER_SCHEMES = ['year', 'customer', 'plain'];
export const NUMBER_SCHEME_LABELS = { year: 'Jahr und Nummernserie', customer: 'Kundennummer und Nummernserie', plain: 'Nur Nummernserie' };
export const DEFAULT_NUMBER_SCHEME = 'year';
export const NUMBER_KIND_LABELS = { invoice_out: 'Rechnungen', credit_note: 'Gutschriften', cancellation: 'Stornorechnungen' };
export const NUMBER_KINDS = Object.keys(DOCUMENT_NUMBER_PREFIXES);
export const NUMBER_START_MAX = 999_999_999;
/** Nummer nach Schema. `customerNumber` nur für `customer`, `year` nur für `year`. Ohne `prefix` gilt die Standardkennung der Belegart (RE, GS, ST). */
export function formatNumberByScheme(scheme, kind, parts) {
    const seq = String(parts.sequence).padStart(4, '0');
    const prefix = parts.prefix || DOCUMENT_NUMBER_PREFIXES[kind];
    return scheme === 'year' ? `${prefix}-${parts.year}-${seq}` : scheme === 'customer' ? `${prefix}-${parts.customerNumber}-${seq}` : `${prefix}-${seq}`;
}
/** Schlüssel des Zählers: bei `year` ein Zähler je Jahr, sonst ein durchlaufender. Bleibt für `year` wie bisher, damit bestehende Zähler weiterlaufen. */
export function numberCounterKey(scheme, kind, year) {
    return scheme === 'year' ? `finance:${kind}:${year}` : `finance:${kind}:${scheme}`;
}
const startValue = z.number('Bitte eine Zahl angeben').int('Bitte eine ganze Zahl angeben').min(1, 'Mindestens 1').max(NUMBER_START_MAX, `Höchstens ${NUMBER_START_MAX}`);
export const numberingInput = z
    .object({
    scheme: z.enum(NUMBER_SCHEMES, 'Bitte ein Schema wählen'),
    starts: z.object({ invoice_out: startValue, credit_note: startValue, cancellation: startValue }).strict(),
    /** Der Mandant hat die Hinweise gelesen. Pflicht, sobald es ausgestellte Belege gibt. */
    confirm: z.boolean().optional(),
})
    .strict();
/** Erste und letzte Nummer der Personenkonten bei gegebener Länge der Sachkonten */
export function personAccountRange(kind, accountLength) {
    const unit = 10 ** accountLength;
    return kind === 'debtor' ? { first: unit, last: 7 * unit - 1 } : { first: 7 * unit, last: 10 * unit - 1 };
}
// ---------------------------------------------------------------------------
// Eigene Kennungen (Präfixe) für alle Nummernkreise (Änderungswunsch vom 19.10.2026): Einrichtung → Nummernkennungen
// ---------------------------------------------------------------------------
/**
 * Alle Dinge, die eine laufende Nummer bekommen. Jedes hat eine Standardkennung, die der Mandant durch eine eigene ersetzen kann, zum Beispiel „UNIQ“ statt „RE“. Die Kennung steht vorn,
 * der Rest der Nummer (Jahr, Serie) bleibt wie er ist. Eine Änderung gilt nur für neue Nummern: Was schon vergeben ist, ändert sich nie. Ausgenommen sind Debitoren und Kreditoren, deren
 * Nummern Konten im Kontenrahmen sind und deshalb reine Ziffern bleiben müssen.
 */
export const NUMBER_OBJECTS = [
    { key: 'invoice_out', label: 'Rechnungen', defaultPrefix: 'RE', pattern: 'year-sequence', group: 'Finanzen' },
    { key: 'credit_note', label: 'Gutschriften', defaultPrefix: 'GS', pattern: 'year-sequence', group: 'Finanzen' },
    { key: 'cancellation', label: 'Stornorechnungen', defaultPrefix: 'ST', pattern: 'year-sequence', group: 'Finanzen' },
    { key: 'offer', label: 'Angebote', defaultPrefix: 'AN', pattern: 'year-sequence', group: 'Vertrieb' },
    { key: 'order', label: 'Aufträge', defaultPrefix: 'AU', pattern: 'year-sequence', group: 'Vertrieb' },
    { key: 'inquiry', label: 'Anfragen', defaultPrefix: 'A', pattern: 'year-sequence6', group: 'Vertrieb' },
    { key: 'contract', label: 'Verträge', defaultPrefix: 'V', pattern: 'year-sequence', group: 'Verträge' },
    { key: 'termination', label: 'Kündigungen', defaultPrefix: 'K', pattern: 'year-sequence', group: 'Verträge' },
    { key: 'employee', label: 'Personalnummern', defaultPrefix: '', pattern: 'number', group: 'Personal' },
    { key: 'vehicle', label: 'Fahrzeuge', defaultPrefix: 'FZ', pattern: 'sequence', group: 'Betrieb' },
    { key: 'asset', label: 'Inventar', defaultPrefix: 'IN', pattern: 'sequence', group: 'Betrieb' },
    { key: 'financing', label: 'Finanzierungen und Leasings', defaultPrefix: 'FL', pattern: 'sequence', group: 'Finanzen' },
    { key: 'financing_project', label: 'Finanzierungsvorhaben', defaultPrefix: 'FV', pattern: 'sequence', group: 'Finanzen' },
    { key: 'inventory_count', label: 'Inventuren', defaultPrefix: 'IV', pattern: 'year-sequence', group: 'Betrieb' },
];
export const NUMBER_OBJECT_KEYS = NUMBER_OBJECTS.map((o) => o.key);
export const DEFAULT_NUMBER_PREFIXES = Object.fromEntries(NUMBER_OBJECTS.map((o) => [o.key, o.defaultPrefix]));
export const NUMBER_PREFIX_MAX = 10;
/**
 * Nummer eines Objekts mit seiner Kennung. Jahr-Serie: UNIQ-2026-0001, Serie: FZ-0001, Personalnummer: 1001 oder mit Kennung P-1001. Eine leere Kennung ergibt bei Personalnummern die reine Zahl,
 * bei allen anderen die Standardkennung.
 */
export function formatObjectNumber(key, prefix, parts) {
    const def = NUMBER_OBJECTS.find((o) => o.key === key);
    const p = (prefix ?? '').trim() || def.defaultPrefix;
    const seq = String(parts.sequence).padStart(def.pattern === 'year-sequence6' ? 6 : 4, '0');
    switch (def.pattern) {
        case 'year-sequence':
        case 'year-sequence6':
            return `${p}-${parts.year}-${seq}`;
        case 'sequence':
            return `${p}-${seq}`;
        default:
            return p ? `${p}-${parts.sequence}` : String(parts.sequence);
    }
}
const prefixField = z
    .string()
    .trim()
    .toUpperCase()
    .refine((v) => v === '' || new RegExp(`^[A-Z0-9]{1,${NUMBER_PREFIX_MAX}}$`).test(v), `Nur Buchstaben und Ziffern, höchstens ${NUMBER_PREFIX_MAX} Zeichen, ohne Leer- und Sonderzeichen.`)
    .nullable();
/** Eigene Kennungen je Objekt. Leer oder null: die Standardkennung gilt. Immer alle Schlüssel, die Angabe ersetzt die bisherige vollständig. */
export const numberPrefixesInput = z
    .object(Object.fromEntries(NUMBER_OBJECT_KEYS.map((k) => [k, prefixField])))
    .strict()
    .superRefine((v, ctx) => {
    // Rechnung, Gutschrift und Storno teilen sich die Tabelle der Belege. Mit gleicher Kennung entstünden gleiche Nummern.
    const finance = ['invoice_out', 'credit_note', 'cancellation'];
    const effective = finance.map((k) => (v[k] || DEFAULT_NUMBER_PREFIXES[k]).toUpperCase());
    finance.forEach((k, i) => {
        if (effective.some((e, j) => j !== i && e === effective[i]))
            ctx.addIssue({ code: 'custom', path: [k], message: 'Rechnungen, Gutschriften und Stornorechnungen brauchen verschiedene Kennungen, sonst könnten zwei Belege dieselbe Nummer tragen.' });
    });
});
//# sourceMappingURL=numbers.js.map